import React, {useEffect, useMemo, useState} from 'react';
import {useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {PageHeader} from "../../../../components/PageHeader";
import {refresh} from "../../../../redux/slice/apiSlice";
import {callAsyncHandler} from "../../../../components/commonmethod/CommonMethod";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {useCacheRefresh} from "../../../../hooks/useCacheRefresh";
import {CacheType} from "../../../../constant/CodeSpecNum";
import {
    createCustomCode,
    createCustomString,
    createGroupSelect,
    deleteCustomCode,
    deleteCustomString,
    deleteGroupSelect,
    getCustomCodeList,
    getCustomStringList,
    getGroupSelectList,
    updateCustomCode,
    updateCustomString,
    updateGroupSelect,
} from "../../../../api/field/CustomCodeService";
import {
    createProjectCode,
    deleteProjectCode,
    getProjectCodeList,
    updateProjectCode,
} from "../../../../api/projectcode/ProjectCodeService";
import {getFields} from "../../../../api/field/FieldService";
import CodeManageSettingsLayout from "../shared/CodeManageSettingsLayout";
import CodeManageItemList from "../shared/CodeManageItemList";
import CommonCodeEditor from "../shared/CommonCodeEditor";
import CodeEditorPanel from "../shared/CodeEditorPanel";
import GroupCodeEditor from "../../groupCode/GroupCodeEditor";
import {
    buildCustomCodeSidebarNavItems,
    CUSTOM_CODE_CATEGORIES,
    CUSTOM_CODE_ROUTE_CATEGORY,
    isAssetItemGroupCodeCategory,
} from "../../groupCode/groupCodeCategoryConfig";
import {
    filterCategoriesByPlatform,
    pickActiveCategoryId,
} from "../../groupCode/menuPlatformFilter";
import {useGroupCodeCategory} from "../../groupCode/useGroupCodeCategory";
import "../shared/unifiedCodeManage.css";
import "../shared/codeManageSettings.css";
import "../../groupCode/groupCodeManage.css";

const CREATE_TYPE_OPTIONS = [
    {id: "code", label: "코드"},
    {id: "text", label: "텍스트"},
];

const ASSET_GROUP_PANEL_SUB_NAV = [
    {id: "codeManage", label: "코드관리"},
    {id: "selectOption", label: "선택옵션관리"},
];

function withItemKind(item, itemKind) {
    return {...item, itemKind};
}

function getItemKey(item) {
    if (!item) {
        return null;
    }
    return item.itemKind ? `${item.itemKind}:${item.id}` : String(item.id);
}

function CustomCode() {
    const location = useLocation();
    const platFrom = useSelector((state) => state.auth.platFrom);

    const visibleCategories = useMemo(
        () => filterCategoriesByPlatform(CUSTOM_CODE_CATEGORIES, platFrom),
        [platFrom],
    );

    const sidebarNavItems = useMemo(
        () => buildCustomCodeSidebarNavItems(visibleCategories),
        [visibleCategories],
    );

    const [activeCategory, setActiveCategory] = useState(() =>
        pickActiveCategoryId(
            CUSTOM_CODE_CATEGORIES,
            CUSTOM_CODE_ROUTE_CATEGORY[location.pathname] ?? "project",
            platFrom,
        ),
    );
    const [commonData, setCommonData] = useState([]);
    const [projectData, setProjectData] = useState([]);
    const [fieldData, setFieldData] = useState([]);
    const [groupData, setGroupData] = useState([]);
    const [groupFields, setGroupFields] = useState(null);
    const [fieldFlag, setFieldFlag] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [assetGroupPanelView, setAssetGroupPanelView] = useState("codeManage");

    const status = useSelector(state => state.api.status);
    const dispatch = useDispatch();
    const refreshCache = useCacheRefresh();

    const activeMeta = useMemo(
        () => visibleCategories.find((c) => c.id === activeCategory)
            ?? visibleCategories[0]
            ?? null,
        [activeCategory, visibleCategories],
    );

    const groupCodeCategoryMeta = useMemo(
        () => (isAssetItemGroupCodeCategory(activeCategory) ? activeMeta : null),
        [activeCategory, activeMeta],
    );
    const groupCode = useGroupCodeCategory(groupCodeCategoryMeta);

    const handleCategoryChange = (categoryId) => {
        setActiveCategory(categoryId);
    };

    const commonItemList = useMemo(() => {
        const codes = (commonData ?? []).map((item) => withItemKind(item, "code"));
        const fields = (fieldData ?? []).map((item) => withItemKind(item, "text"));
        return [...codes, ...fields].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "ko"));
    }, [commonData, fieldData]);

    const listData = useMemo(() => {
        if (activeCategory === "project") {
            return projectData;
        }
        if (activeCategory === "commonItem") {
            return commonItemList;
        }
        if (activeCategory === "assetGroup" && assetGroupPanelView === "selectOption") {
            return groupData;
        }
        if (activeCategory === "groupSelectOption") {
            return groupData;
        }
        return [];
    }, [activeCategory, assetGroupPanelView, projectData, commonItemList, groupData]);

    const selectedItemKey = useMemo(() => getItemKey(selectedItem), [selectedItem]);

    useEffect(() => {
        let preferred;
        if (location.pathname === "/settings/custom/group-code") {
            preferred = "assetGroup";
            setAssetGroupPanelView("selectOption");
        } else if (location.pathname === "/settings/custom/group-field") {
            preferred = "assetGroup";
            setAssetGroupPanelView("codeManage");
        } else {
            preferred = CUSTOM_CODE_ROUTE_CATEGORY[location.pathname] ?? activeCategory;
        }
        const nextId = pickActiveCategoryId(CUSTOM_CODE_CATEGORIES, preferred, platFrom);
        if (nextId && nextId !== activeCategory) {
            setActiveCategory(nextId);
        }
    }, [location.pathname, platFrom, visibleCategories]);

    useEffect(() => {
        setSelectedItem(null);
        if (activeCategory !== "assetGroup") {
            setAssetGroupPanelView("codeManage");
        }
    }, [activeCategory]);

    useEffect(() => {
        setSelectedItem(null);
    }, [assetGroupPanelView]);

    const isAssetGroupSelectOption = activeCategory === "assetGroup" && assetGroupPanelView === "selectOption";
    const isGroupCodePanel = !!groupCode && !isAssetGroupSelectOption;

    const loadCommon = async () => {
        const data = await callAsyncHandler(getCustomCodeList);
        if (data !== null) {
            setCommonData(data);
        }
        return data;
    };

    const loadProject = async () => {
        try {
            const response = await getProjectCodeList();
            setProjectData(response.data.content ?? []);
            return response.data.content;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    const loadField = async () => {
        const data = await callAsyncHandler(getCustomStringList);
        if (data !== null) {
            setFieldData(data);
        }
        return data;
    };

    const loadGroupCodes = async () => {
        const data = await callAsyncHandler(getGroupSelectList);
        if (data !== null) {
            setGroupData(data);
        }
        return data;
    };

    const loadGroupFieldDefinitions = async () => {
        try {
            const response = await getFields();
            if (response.status === 200) {
                setGroupFields(response.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        Promise.all([loadCommon(), loadProject(), loadField(), loadGroupCodes()]);
        dispatch(refresh(false));
    }, [status]);

    useEffect(() => {
        loadGroupFieldDefinitions();
        setFieldFlag(false);
    }, [fieldFlag]);

    const refreshCommonSelection = async () => {
        const data = await loadCommon();
        setSelectedItem((prev) => {
            if (!prev || prev.itemKind !== "code") {
                return prev;
            }
            const next = data?.find((each) => each.id === prev.id);
            return next ? withItemKind(next, "code") : null;
        });
        return data;
    };

    const refreshFieldSelection = async () => {
        const data = await loadField();
        setSelectedItem((prev) => {
            if (!prev || prev.itemKind !== "text") {
                return prev;
            }
            const next = data?.find((each) => each.id === prev.id);
            return next ? withItemKind(next, "text") : null;
        });
        return data;
    };

    const syncSelection = async (loader, itemKind) => {
        const data = await loader();
        setSelectedItem((prev) => {
            if (!prev) {
                return prev;
            }
            if (itemKind && prev.itemKind !== itemKind) {
                return prev;
            }
            const next = data?.find((each) => each.id === prev.id);
            if (!next) {
                return null;
            }
            return itemKind ? withItemKind(next, itemKind) : next;
        });
    };

    const refreshGroupSelection = async () => {
        const data = await loadGroupCodes();
        setSelectedItem((prev) => {
            if (!prev) {
                return prev;
            }
            const next = data?.find((each) => each.id === prev.id);
            return next ?? null;
        });
        return data;
    };

    const handleCommonOptionCreate = async (parentId, name) => {
        const _ = await createCustomCode({parentId, parent: {id: parentId}, name});
        if (_.status === 200) {
            handleActiveToast({message: "값이 등록되었습니다."});
            refreshCache(CacheType.customCode);
            await refreshCommonSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleCommonOptionUpdate = async (item) => {
        const _ = await updateCustomCode(item);
        if (_.status === 200) {
            handleActiveToast({message: "값이 수정되었습니다."});
            refreshCache(CacheType.customCode);
            await refreshCommonSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleCommonOptionDelete = async (id) => {
        const _ = await deleteCustomCode(id);
        if (_.status === 200) {
            handleActiveToast({message: "값이 삭제되었습니다."});
            refreshCache(CacheType.customCode);
            await refreshCommonSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleCommonCreate = async (name) => {
        const _ = await createCustomCode({name, target: "assetCustomCodes"});
        if (_.status === 200) {
            handleActiveToast({message: "등록 되었습니다."});
            refreshCache(CacheType.customCode);
            await loadCommon();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleCommonUpdate = async (item) => {
        const _ = await updateCustomCode(item);
        if (_.status === 200) {
            handleActiveToast({message: "수정 되었습니다."});
            refreshCache(CacheType.customCode);
            await refreshCommonSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleCommonDelete = async (id) => {
        const _ = await deleteCustomCode(id);
        if (_.status === 200) {
            handleActiveToast({message: "삭제 되었습니다."});
            refreshCache(CacheType.customCode);
            setSelectedItem((prev) => (prev?.id === id && prev?.itemKind === "code" ? null : prev));
            await loadCommon();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleProjectCreate = async (name) => {
        const _ = await createProjectCode({name});
        if (_.status === 200) {
            handleActiveToast({message: "등록되었습니다."});
            refreshCache(CacheType.project);
            await loadProject();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleProjectUpdate = async (item) => {
        const _ = await updateProjectCode(item);
        if (_.status === 200) {
            handleActiveToast({message: "수정되었습니다."});
            refreshCache(CacheType.project);
            await syncSelection(loadProject);
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleProjectDelete = async (id) => {
        const _ = await deleteProjectCode(id);
        if (_.status === 200) {
            handleActiveToast({message: "삭제되었습니다."});
            refreshCache(CacheType.project);
            setSelectedItem((prev) => (prev?.id === id ? null : prev));
            await loadProject();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleFieldCreate = async (name) => {
        const _ = await createCustomString({name, target: "assetCustomStrings"});
        if (_.status === 200) {
            handleActiveToast({message: "등록 되었습니다."});
            await loadField();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleFieldUpdate = async (item) => {
        const _ = await updateCustomString(item);
        if (_.status === 200) {
            handleActiveToast({message: "수정 되었습니다."});
            await refreshFieldSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleFieldDelete = async (id) => {
        const _ = await deleteCustomString(id);
        if (_.status === 200) {
            handleActiveToast({message: "삭제 되었습니다."});
            setSelectedItem((prev) => (prev?.id === id && prev?.itemKind === "text" ? null : prev));
            await loadField();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleCommonItemCreate = async (name, type) => {
        if (type === "text") {
            await handleFieldCreate(name);
            return;
        }
        await handleCommonCreate(name);
    };

    const handleGroupCreate = async (payload) => {
        const _ = await createGroupSelect(typeof payload === "string" ? {name: payload} : payload);
        if (_.status === 200) {
            handleActiveToast({message: "등록 되었습니다."});
            await loadGroupCodes();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleGroupOptionCreate = async (parentId, name) => {
        const _ = await createGroupSelect({parentId, parent: {id: parentId}, name});
        if (_.status === 200) {
            handleActiveToast({message: "값이 등록되었습니다."});
            await refreshGroupSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleGroupOptionUpdate = async (item) => {
        const _ = await updateGroupSelect(item);
        if (_.status === 200) {
            handleActiveToast({message: "값이 수정되었습니다."});
            await refreshGroupSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleGroupOptionDelete = async (id) => {
        const _ = await deleteGroupSelect(id);
        if (_.status === 200) {
            handleActiveToast({message: "값이 삭제되었습니다."});
            await refreshGroupSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleGroupUpdate = async (item) => {
        const _ = await updateGroupSelect(item);
        if (_.status === 200) {
            handleActiveToast({message: "수정 되었습니다."});
            await refreshGroupSelection();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const handleGroupDelete = async (id) => {
        const _ = await deleteGroupSelect(id);
        if (_.status === 200) {
            handleActiveToast({message: "삭제 되었습니다."});
            setSelectedItem((prev) => (prev?.id === id ? null : prev));
            await loadGroupCodes();
        } else if (_.status === 201 || _.status === 202) {
            handleActiveError({message: _.data.title, description: _.data.reason});
        }
    };

    const listConfig = useMemo(() => {
        if (activeCategory === "project") {
            return {
                addLabel: "시스템",
                addPlaceholder: "예: 시스템-A",
                onCreate: handleProjectCreate,
            };
        }
        if (activeCategory === "commonItem") {
            return {
                addLabel: "공통 항목",
                addPlaceholder: "예: IP(하드웨어), 비고",
                onCreate: handleCommonItemCreate,
                createTypeOptions: CREATE_TYPE_OPTIONS,
            };
        }
        if (isAssetGroupSelectOption || activeCategory === "groupSelectOption") {
            return {
                addLabel: "Select 옵션",
                addPlaceholder: "예: 장비유형, 설치위치",
                onCreate: handleGroupCreate,
            };
        }
        return {};
    }, [activeCategory, isAssetGroupSelectOption]);

    const renderGroupSelectEditor = () => {
        if (!selectedItem) {
            return <CodeEditorPanel/>;
        }

        return (
            <CommonCodeEditor
                item={selectedItem}
                setItem={setSelectedItem}
                onSave={handleGroupUpdate}
                onDelete={handleGroupDelete}
                onCreateOption={handleGroupOptionCreate}
                onUpdateOption={handleGroupOptionUpdate}
                onDeleteOption={handleGroupOptionDelete}
            />
        );
    };

    const renderEditor = () => {
        if (!selectedItem) {
            return <CodeEditorPanel/>;
        }

        if (activeCategory === "commonItem" && selectedItem.itemKind === "code") {
            return (
                <CommonCodeEditor
                    item={selectedItem}
                    setItem={setSelectedItem}
                    onSave={handleCommonUpdate}
                    onDelete={handleCommonDelete}
                    onCreateOption={handleCommonOptionCreate}
                    onUpdateOption={handleCommonOptionUpdate}
                    onDeleteOption={handleCommonOptionDelete}
                />
            );
        }

        if (activeCategory === "commonItem" && selectedItem.itemKind === "text") {
            return (
                <CodeEditorPanel
                    category="field"
                    item={selectedItem}
                    setItem={setSelectedItem}
                    onSave={handleFieldUpdate}
                    onDelete={handleFieldDelete}
                    nameLabel="필드명"
                    formType="string"
                    viewMode
                />
            );
        }

        if (activeCategory === "project") {
            return (
                <CodeEditorPanel
                    category="project"
                    item={selectedItem}
                    setItem={setSelectedItem}
                    onSave={handleProjectUpdate}
                    onDelete={handleProjectDelete}
                    nameLabel="시스템명"
                    formType="project"
                    viewMode
                />
            );
        }

        if (activeCategory === "groupSelectOption") {
            return (
                <CommonCodeEditor
                    item={selectedItem}
                    setItem={setSelectedItem}
                    onSave={handleGroupUpdate}
                    onDelete={handleGroupDelete}
                    onCreateOption={handleGroupOptionCreate}
                    onUpdateOption={handleGroupOptionUpdate}
                    onDeleteOption={handleGroupOptionDelete}
                />
            );
        }

        return <CodeEditorPanel/>;
    };

    if (!visibleCategories.length) {
        return (
            <div className="common-container container-height-full">
                <PageHeader
                    header="자산항목관리"
                    indicator="환경설정 > 코드관리 > 자산코드 > 자산항목관리"
                />
                <p className="group-code-manage__empty-platform">
                    현재 플랫폼({platFrom ?? "-"})에서 사용할 수 있는 자산코드 항목이 없습니다.
                </p>
            </div>
        );
    }

    if (!activeMeta) {
        return null;
    }

    return (
        <div className="common-container container-height-full">
            <PageHeader
                header="자산항목관리"
                indicator="환경설정 > 코드관리 > 자산코드 > 자산항목관리"
            />

            <CodeManageSettingsLayout
                navItems={sidebarNavItems}
                activeCategory={activeCategory}
                onCategoryChange={handleCategoryChange}
                panelTitle={activeMeta.title}
                panelDescription={activeMeta.description}
                panelSubNav={activeCategory === "assetGroup" ? ASSET_GROUP_PANEL_SUB_NAV : null}
                panelSubNavValue={assetGroupPanelView}
                onPanelSubNavChange={setAssetGroupPanelView}
                list={
                    isGroupCodePanel ? (
                        <CodeManageItemList
                            addLabel={activeMeta.addLabel}
                            createSectionLabel="코드 추가"
                            items={groupCode.listData}
                            selectedItemId={groupCode.selectedCode?.id}
                            onSelect={groupCode.handleSelectCode}
                            onCreateRoot={groupCode.handleCreateRoot}
                            useAssetGroupTree={activeMeta.supportsChildCodes !== false}
                            emptyMessage="등록된 코드가 없습니다. 아래에서 추가하세요."
                        />
                    ) : (
                    <CodeManageItemList
                        addLabel={listConfig.addLabel}
                        addPlaceholder={listConfig.addPlaceholder}
                        items={listData}
                        selectedItemId={selectedItem?.id}
                        selectedItemKey={selectedItemKey}
                        onSelect={setSelectedItem}
                        onCreateRoot={listConfig.onCreate}
                        createTypeOptions={listConfig.createTypeOptions}
                        useAssetGroupTree={listConfig.useAssetGroupTree}
                        emptyMessage={listConfig.emptyMessage}
                        showItemKind={activeCategory === "commonItem"}
                    />
                    )
                }
                editor={
                    isGroupCodePanel ? (
                        <GroupCodeEditor
                            categoryKind={activeMeta.kind}
                            assetCodeType={activeMeta.assetCodeType}
                            nameLabel={activeMeta.nameLabel}
                            supportsChildCodes={activeMeta.supportsChildCodes !== false}
                            code={groupCode.selectedCode}
                            setCode={groupCode.setSelectedCode}
                            onUpdate={groupCode.handleUpdate}
                            onDelete={groupCode.handleDelete}
                            onCreateChild={groupCode.handleCreateChild}
                            onUpdateChild={groupCode.handleUpdateChild}
                            onDeleteChild={groupCode.handleDelete}
                            groupFields={activeCategory === "assetGroup" ? groupFields : null}
                            setFieldFlag={activeCategory === "assetGroup" ? setFieldFlag : undefined}
                        />
                    ) : isAssetGroupSelectOption ? (
                        renderGroupSelectEditor()
                    ) : renderEditor()
                }
            />
        </div>
    );
}

export default CustomCode;
